import React, { useState } from "react";
import {
    Input,
    Button,
    Row,
    Col,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import moment from "moment";
import { toast } from "react-hot-toast";
import { X } from "react-feather";
import axios from "axios";

const AddUnavailableDates = ({ product, onSave }) => {
    const initialUnavailableDates = [
        { id: 6, from_date: "2025-06-04", to_date: "2025-06-07" },
        { id: 3, from_date: "2025-06-10", to_date: "2025-06-12" },
    ];

    const booked_dates = ["2025-06-15"];

    const [unavailableDates, setUnavailableDates] = useState(product.unavailable_ranges);
    const [removedIndexes, setRemovedIndexes] = useState([]);
    const [newDateRanges, setNewDateRanges] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const handleNewChange = (index, field, value) => {
        const updated = [...newDateRanges];
        updated[index][field] = value;
        setNewDateRanges(updated);
    };

    const removeExistingDate = (id) => {
        setUnavailableDates((prev) => prev.filter((d) => d.id !== id));
        setRemovedIndexes((prev) => [...prev, id]);
    };

    const removeNewlyAdded = (index) => {
        const updated = [...newDateRanges];
        updated.splice(index, 1);
        setNewDateRanges(updated);
    };

    const validateRange = ({ from, to }) => {
        const fromDate = moment(from);
        const toDate = moment(to);

        if (!from || !to) {
            toast.error("Veuillez remplir les deux dates.");
            return false;
        }

        if (!fromDate.isValid() || !toDate.isValid()) {
            toast.error("Les dates ne sont pas valides.");
            return false;
        }

        if (fromDate.isAfter(toDate)) {
            toast.error("La date de début doit être avant la date de fin.");
            return false;
        }

        // ✅ BLOCK: Around booking dates
        for (const booked of product.booked_dates) {
            const bookedMoment = moment(booked);

            const blockStart = bookedMoment.clone().subtract(2, "days");
            const blockEnd = bookedMoment.clone().add(3, "days");

            const overlapsWithBookingWindow =
                fromDate.isBetween(blockStart, blockEnd, "day", "[]") ||
                toDate.isBetween(blockStart, blockEnd, "day", "[]") ||
                blockStart.isBetween(fromDate, toDate, "day", "[]") ||
                blockEnd.isBetween(fromDate, toDate, "day", "[]");

            if (overlapsWithBookingWindow) {
                toast.error(
                    `Les dates sélectionnées ne doivent pas être comprises entre le ${blockStart.format("YYYY-MM-DD")} et le ${blockEnd.format("YYYY-MM-DD")} (réservation).`
                );
                return false;
            }
        }

        // ✅ BLOCK: Overlap with existing unavailable ranges
        for (const existing of unavailableDates) {
            const existingFrom = moment(existing.start_date || existing.from_date);
            const existingTo = moment(existing.end_date || existing.to_date);

            const overlaps =
                fromDate.isBetween(existingFrom, existingTo, "day", "[]") ||
                toDate.isBetween(existingFrom, existingTo, "day", "[]") ||
                existingFrom.isBetween(fromDate, toDate, "day", "[]") ||
                existingTo.isBetween(fromDate, toDate, "day", "[]");

            if (overlaps) {
                toast.error(
                    `Ces dates chevauchent une période déjà indisponible (${existingFrom.format("YYYY-MM-DD")} → ${existingTo.format("YYYY-MM-DD")}). Supprimez-la d'abord pour en ajouter une nouvelle.`
                );
                return false;
            }
        }

        return true;
    };



    const addNewRange = () => {
        const last = newDateRanges[newDateRanges.length - 1];
        if (!last || validateRange(last)) {
            setNewDateRanges([...newDateRanges, { from: "", to: "" }]);
        }
    };

    const handleSubmit = () => {
        for (const range of newDateRanges) {
            if (!validateRange(range)) return;
        }

        if (newDateRanges.length === 0 && removedIndexes.length === 0) {
            toast.error("Aucun changement détecté.");
            return;
        }

        toggleModal();
    };

    const handleConfirm = async () => {
        const payload = {
            removed_indexes: removedIndexes,
            newly_created: newDateRanges.map(({ from, to }) => ({
                from_date: from,
                to_date: to,
            })),
        };

        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_BASE_URL}/product/${product.id}/unavailable-dates/`,
                payload
            );

            toast.success(response.data.message);
            setNewDateRanges([]);
            setRemovedIndexes([]);
            toggleModal();

            onSave();
        } catch (error) {
            console.log(error);
            toast.error(error.response.data.message);
        }
    };



    return (
        <div>
            {unavailableDates.length > 0 && (
                <div className="mb-3">
                    {/* <h6 className="mb-2">Dates déjà indisponibles :</h6> */}
                    {unavailableDates.map((d) => (
                        <div className="date-row" key={d.id}>
                            <span>{d.start_date} → {d.end_date}</span>
                            <Button close size="sm" className="d-flex align-items-center justify-content-center bg-danger-subtle p-1 px-2 rounded-1 border-0" onClick={() => removeExistingDate(d.id)}>
                                <X size={14} />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {newDateRanges.map((range, idx) => (
                <Row key={idx} className="mb-2 align-items-center">
                    <Col md={5}>
                        <Input
                            type="date"
                            value={range.from}
                            min={moment().format("YYYY-MM-DD")}
                            onChange={(e) => handleNewChange(idx, "from", e.target.value)}
                        />
                    </Col>
                    <Col md={5}>
                        <Input
                            type="date"
                            value={range.to}
                            min={moment().format("YYYY-MM-DD")}
                            onChange={(e) => handleNewChange(idx, "to", e.target.value)}
                        />
                    </Col>
                    <Col md={2}>
                        <Button close className="d-flex align-items-center justify-content-center bg-danger-subtle p-1 px-2 rounded-1 border-0" onClick={() => removeNewlyAdded(idx)} size="sm">
                            <X size={14} />
                        </Button>
                    </Col>
                </Row>
            ))}

            {unavailableDates.length > 0 || newDateRanges.length > 0 || removedIndexes.length > 0 ? (
                <div className="d-flex gap-2 mt-2 flex-wrap">
                    <Button size="sm" color="secondary" onClick={addNewRange}>
                        Ajouter plus
                    </Button>
                    <Button size="sm" color="success" onClick={handleSubmit}>
                        Enregistrer les modifications
                    </Button>
                </div>
            ) : (

                // {unavailableDates.length === 0 && newDateRanges.length === 0 && (
                <Button size="sm" color="dark" onClick={addNewRange} className="mt-2">
                    Ajouter une date
                </Button>
                // )}
            )}

            {/* Confirmation Modal */}
            <Modal isOpen={isModalOpen} toggle={toggleModal}>
                <ModalBody>
                    Vous êtes sur le point de modifier les dates d’indisponibilité de ce produit.
                    <br />
                    <br />
                    <strong>Ces modifications seront visibles par les utilisateurs</strong> et
                    pourraient affecter les réservations.
                    <br />
                    <br />
                    Voulez-vous vraiment continuer ?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        Annuler
                    </Button>
                    <Button color="primary" onClick={handleConfirm}>
                        Confirmer et enregistrer
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default AddUnavailableDates;
